import { useEffect, useRef, useState } from 'react';
import { prefersReducedMotion } from '../lib/gsap';
import { canWebGL } from './Journey';
import { StaticChip } from './StaticVisuals';
import { transit } from './SceneTransition';

export const ServiceScene3D = ({ kind, Fallback }) => {
  const [mode, setMode] = useState(() => (prefersReducedMotion() || !canWebGL() ? 'static' : 'loading'));
  const holder = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (mode === 'static') return undefined;
    let exp = null;
    let disposed = false;
    const cleanups = [];
    const mobile = window.innerWidth < 1024;
    const size = () => {
      if (!exp) return;
      exp.resize(holder.current.clientWidth, holder.current.clientHeight);
      holder.current.dataset.chipRatio = exp.chipRatio().toFixed(3);
    };
    const onPlay = () => exp && exp.play();
    window.addEventListener('sm:scene-play', onPlay);
    cleanups.push(() => window.removeEventListener('sm:scene-play', onPlay));
    const onScroll = () => exp && exp.setTarget(window.scrollY / (window.innerHeight * 0.6));
    const onPointer = (e) => { if (exp && e.pointerType === 'mouse') exp.setPointer(e.clientX / window.innerWidth - 0.5, e.clientY / window.innerHeight - 0.5); };

    import('../three/ServiceScene')
      .then(({ createServiceScene }) => createServiceScene(canvasRef.current, { kind, mobile, hold: true, onLost: () => !disposed && setMode('static') }))
      .then((e) => {
        if (disposed) { e.dispose(); return; }
        exp = e;
        size();
        if (!transit.active) exp.play();
        onScroll();
        setMode('webgl');
        const ro = new ResizeObserver(size);
        ro.observe(holder.current);
        const io = new IntersectionObserver(([en]) => exp && exp.setActive(en.isIntersecting && !document.hidden));
        io.observe(holder.current);
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('pointermove', onPointer, { passive: true });
        cleanups.push(() => {
          ro.disconnect(); io.disconnect();
          window.removeEventListener('scroll', onScroll);
          window.removeEventListener('pointermove', onPointer);
        });
      })
      .catch(() => !disposed && setMode('static'));

    return () => {
      disposed = true;
      cleanups.forEach((c) => c());
      if (exp) exp.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, mode === 'static']);

  if (mode === 'static') return <Fallback active />;

  return (
    <div ref={holder} className="relative w-full h-full pointer-events-none" data-testid={`service-scene-${kind}`} data-scene-mode={mode}>
      <canvas ref={canvasRef} className="block w-full h-full transition-opacity duration-700" style={{ opacity: mode === 'webgl' ? 1 : 0 }} data-testid="service-scene-canvas" />
      {mode === 'loading' && (
        <div className="absolute inset-0 flex items-center justify-center">
          <StaticChip className="w-[44%] opacity-80" />
        </div>
      )}
    </div>
  );
};
